import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './tmi.css';
import './Packlists.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    TreeList,
    TreeListExpandChangeEvent,
    TreeListSortChangeEvent,
    TreeListFilterChangeEvent,
    TreeListItemChangeEvent,
    TreeListCellProps,
    TreeListTextEditor,
    TreeListSelectionCell,
    TreeListHeaderSelectionCell,
    TreeListSelectionChangeEvent,
    TreeListHeaderSelectionChangeEvent,
    mapTree,
    extendDataItem,
    createDataTree,
    filterBy,
} from '@progress/kendo-react-treelist';
import { orderBy, SortDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { createPortal } from 'react-dom';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type PacklistsProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: PacklistsState;
    onSaveState?: (menuItemId: number, state: PacklistsState) => void;
};

interface PacklistRow {
    packlistID: number;
    displayText:string;
    packlistDesc: string;
    packlistCode: string;
    packlistTypeID:number;
    packlistStatusID: number;
    lastChangedBy: string;
    lastChangedOn: Date;
    parentPacklistID: number | null;
}

interface PacklistType {
    value: string;
    display: string;
}

interface SplPage {
    value: string;
    display: string;
}

interface PacklistItem {
    packlistItemID: string;
    packlistID: number;
    itemID: number;
    itemDesc: string;
    quantity: number;
    itemType: string;
    sundry: boolean;
    lastChangedBy: string;
    lastChangedOn: Date;
}

interface PacklistAttributeInfo {
    packListAttributeValueID: string; // GUID as string
    attributeID: number;
    name: string;
    attributeValue: string;
    lastChangedBy?: string;
    lastChangedOn?: Date | null;
}

interface PacklistsState {
    data: PacklistRow[];
    loading: boolean;
    errorMsg: string;
    expanded: number[];
    sort: SortDescriptor[];
    filter: FilterDescriptor[];
    filterText: string;
    filterTexts: { [key: string]: string };
    headerFocusField: string | null;
    editItemId: number | null;
    originalItem: PacklistRow | null;
    packlistTypes: PacklistType[];
    packlistStatuses: PacklistType[];
    splPages: SplPage[];
    selectedSplPage: SplPage | null;
    selectedIds: Set<number>;
    menuDataItem: PacklistRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    moveItemsDlgOpen: boolean;
    moveItemsDlgLoading: boolean;
    moveItemsDlgError: string;
    moveItemsDlgItems: PacklistItem[];
    moveItemsDlgSelectedIds: Set<string>;
    moveItemsDlgDest: PacklistRow | null;
    newPacklistDlgOpen: boolean;
    newPacklistDlgSaving: boolean;
    newPacklistDlgError: string;
    newPacklistDlgCode: string;
    newPacklistDlgDesc: string;
    newPacklistDlgTypeID: string;
    newPacklistDlgParentID: number | null;
    deleteConfirmItem: PacklistRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    addChildrenDlgOpen: boolean;
    addChildrenDlgLoading: boolean;
    addChildrenDlgError: string;
    addChildrenDlgDest: PacklistRow | null;
    addChildrenDlgItems: PacklistRow[];
    addChildrenDlgSelectedIds: Set<number>;
    attributesDlgOpen: boolean;
    attributesDlgLoading: boolean;
    attributesDlgItems: PacklistAttributeInfo[];
    attributesDlgError: string;
    attributesDlgSavingIds: Set<string>;
    attributesDlgItemErrors: { [key: string]: string };
    attributesDlgOriginalValues: { [key: string]: { attributeValue: string; attributeID: number } };
    attributesDeleteConfirmId: string | null;
    attributesDeleteInProgress: boolean;
    attributesDeleteError: string;
    packlistAttributesForDropDown: { value: number; display: string }[];
    attributesForDropDown: { value: number; display: string }[];
    packlistItemsDlgOpen: boolean;
    packlistItemsDlgLoading: boolean;
    packlistItemsDlgItems: PacklistItem[];
    packlistItemsDlgError: string;
    packlistItemsDlgSavingIds: Set<string>;
    packlistItemsDlgItemErrors: { [key: string]: string };
    packlistItemsDlgColWidths: { [key: string]: number };
    moveItemsDlgColWidths: { [key: string]: number };
    attributesDlgColWidths: { [key: string]: number };
    attributesDlgSort: SortDescriptor[];
    packlistItemAttributesDlgOpen: boolean;
    packlistItemAttributesDlgLoading: boolean;
    packlistItemAttributesDlgItems: PacklistAttributeInfo[];
    packlistItemAttributesDlgError: string;
    packlistItemAttributesDlgColWidths: { [key: string]: number };
    packlistItemAttributesDlgSavingIds: Set<string>;
    packlistItemAttributesDlgItemErrors: { [key: string]: string };
    packlistItemAttributesDlgOriginalValues: { [key: string]: { attributeValue: string; attributeID: number } };
    packlistItemAttributesDeleteConfirmId: string | null;
    packlistItemAttributesDeleteInProgress: boolean;
    packlistItemAttributesDeleteError: string;
}

const DATA_ITEM_KEY = 'packlistID';
const SUB_ITEMS_FIELD = 'items';
const EDIT_FIELD = 'inEdit';
const SELECTED_FIELD = 'selected';

const applyPacklistFilters = (rows: PacklistRow[], filterTexts: { [key: string]: string }, typeLookup: PacklistType[], statusLookup: PacklistType[]): PacklistRow[] => {
    if (Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)) {
        return rows;
    }

    return rows.filter(item => {
        return Object.entries(filterTexts).every(([field, query]) => {
            if (!query || !query.trim()) return true;

            const q = query.trim().toLowerCase();
            let value: string | number | null | undefined;

            if (field === 'packlistTypeID') {
                value = typeLookup.find(t => String(t.value) === String(item.packlistTypeID))?.display ?? '';
            } else if (field === 'packlistStatusID') {
                value = statusLookup.find(s => String(s.value) === String(item.packlistStatusID))?.display ?? '';
            } else {
                value = (item as any)[field];
            }

            return String(value ?? '').toLowerCase().includes(q);
        });
    });
};

const Packlists: React.FC<PacklistsProps> = (props) => {
    const STORAGE_KEY = 'packlists_state_v1';
    const MENU_FLIP_OFFSET = 8;
    const colResizeInfoRef = useRef<{ dialog: 'packlist' | 'move' | 'attributes'; colKey: string; startX: number; startWidth: number } | null>(null);
    const menuBlurTimeoutRef = useRef<any>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const attributeValueRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const headerCellRenderersRef = useRef<Record<string, any>>({});

    // attempt to restore saved UI state from sessionStorage
    const savedRaw = (() => {
        try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
    })();
    const saved = savedRaw ? JSON.parse(savedRaw) : null;

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters) via stateToLoad/onSaveState, so
    // the page doesn't need to refilter/reselect. On first visit (or a full page
    // reload) there is no stateToLoad, so fall back to the sessionStorage-seeded defaults.
    const [state, setState] = useState<PacklistsState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        expanded: saved?.expanded ?? [],
        sort: saved?.sort ?? [{ field: 'displayText', dir: 'asc' }],
        filter: saved?.filter ?? [],
        filterText: saved?.filterText ?? '',
        filterTexts: saved?.filterTexts ?? {},
        headerFocusField: null,
        editItemId: null,
        originalItem: null,
        packlistTypes: [],
        packlistStatuses: [],
        splPages: [],
        selectedSplPage: saved?.selectedSplPage ?? null,
        selectedIds: new Set(saved?.selectedIds ?? []),
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        attributesDlgOpen: false,
        packlistItemsDlgOpen: false,
        packlistItemsDlgLoading: false,
        packlistItemsDlgItems: [],
        packlistItemsDlgError: '',
        packlistItemsDlgSavingIds: new Set(),
        packlistItemsDlgItemErrors: {},
            packlistItemsDlgColWidths: {
                packlistItemID: 120,
                itemID: 90,
                itemDesc: 360,
                quantity: 120,
                itemType: 140,
                lastChangedBy: 160,
                lastChangedOn: 180,
                actions: 80,
            },
        moveItemsDlgColWidths: {
            checkbox: 36,
            itemDesc: 460,
            quantity: 120,
            itemType: 140,
        },
        attributesDlgLoading: false,
        attributesDlgItems: [],
        attributesDlgError: '',
        attributesDlgColWidths: {
            packListAttributeValueID: 120,
            attributeID: 90,
            attributeValue: 720,
            actions: 80,
            lastChangedBy: 160,
            lastChangedOn: 180,
        },
        attributesDlgSort: saved?.attributesDlgSort ?? [{ field: 'attributeID', dir: 'asc' }],
        attributesDlgSavingIds: new Set(),
        attributesDlgItemErrors: {},
        attributesDlgOriginalValues: {},
        attributesDeleteConfirmId: null,
        attributesDeleteInProgress: false,
        attributesDeleteError: '',
        packlistAttributesForDropDown: [],
        attributesForDropDown: [],
        packlistItemAttributesDlgOpen: false,
        packlistItemAttributesDlgLoading: false,
        packlistItemAttributesDlgItems: [],
        packlistItemAttributesDlgError: '',
        packlistItemAttributesDlgColWidths: {
            attributeID: 90,
            attributeValue: 520,
            lastChangedBy: 160,
            lastChangedOn: 180,
            actions: 80,
        },
        packlistItemAttributesDlgSavingIds: new Set(),
        packlistItemAttributesDlgItemErrors: {},
        packlistItemAttributesDlgOriginalValues: {},
        packlistItemAttributesDeleteConfirmId: null,
        packlistItemAttributesDeleteInProgress: false,
        packlistItemAttributesDeleteError: '',
        moveItemsDlgOpen: false,
        moveItemsDlgLoading: false,
        moveItemsDlgError: '',
        moveItemsDlgItems: [],
        moveItemsDlgSelectedIds: new Set(),
        moveItemsDlgDest: null,
        newPacklistDlgOpen: false,
        newPacklistDlgSaving: false,
        newPacklistDlgError: '',
        newPacklistDlgCode: '',
        newPacklistDlgDesc: '',
        newPacklistDlgTypeID: '',
        newPacklistDlgParentID: null,
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        addChildrenDlgOpen: false,
        addChildrenDlgLoading: false,
        addChildrenDlgError: '',
        addChildrenDlgDest: null,
        addChildrenDlgItems: [],
        addChildrenDlgSelectedIds: new Set(),
    });

    // Ref to the latest state (kept in sync on every render below) so the unmount
    // cleanup effect, which only runs once on mount/unmount, can report the
    // up-to-date state rather than a stale snapshot captured on the initial render.
    const stateRef = useRef<PacklistsState>(state);
    stateRef.current = state;

    const saveState = useCallback(() => {
        try {
            const toSave = {
                expanded: state.expanded,
                sort: state.sort,
                filter: state.filter,
                filterText: state.filterText,
                filterTexts: state.filterTexts,
                selectedSplPage: state.selectedSplPage,
                selectedIds: Array.from(state.selectedIds ?? []),
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
        } catch (e) {
            // ignore storage errors
        }
    }, [state.expanded, state.sort, state.filter, state.filterText, state.selectedSplPage, state.selectedIds]);

    useEffect(() => {
        fetchData();
        fetchPacklistTypes();
        fetchPacklistStatuses();
        fetchSplPages();
        return () => {
            // cleanup on unmount
            removeColResizeListeners();
            saveState();
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
            if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    

    const fetchData = (splPageId?: string | number | null) => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklists';
        // Use passed splPageId if provided, otherwise use state value
        const pageId = splPageId !== undefined ? splPageId : (state.selectedSplPage ? state.selectedSplPage.value : "0");
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({ splPageId: pageId }),
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({ ...prev, data: res.results, loading: false }));
                        break;
                    case 'UNAUTH':
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        break;
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + res.callStatusMessage, loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching data: ' + err.toString(), loading: false }));
            });
    }

    const fetchPacklistTypes = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({ ...prev, packlistTypes: res.results }));
                }
            })
            .catch(() => {});
    }

    const fetchSplPages = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistSplPagesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({ ...prev, splPages: res.results }));
                }
            })
            .catch(() => {});
    }


    const fetchPacklistStatuses = () => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetPacklistStatusForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'OK') {
                    setState(prev => ({ ...prev, packlistStatuses: res.results }));
                }
            })
            .catch(() => {});
    }

    
    

    const handleExpandChange = (e: TreeListExpandChangeEvent) => {
        const id: number = e.dataItem[DATA_ITEM_KEY];
        setState(prev => ({ ...prev, expanded: e.value ? prev.expanded.filter(x => x !== id) : [...prev.expanded, id] }));
    }

    const handleSortChange = (e: TreeListSortChangeEvent) => {
        setState(prev => ({ ...prev, sort: e.sort }));
    }

    const handleFilterChange = (e: TreeListFilterChangeEvent) => {
        setState(prev => ({ ...prev, filter: e.filter }));
    }

    const handleFilterTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextVal = e.target.value;
        setState(prev => {
            const next = { ...prev, filterText: nextVal };
            stateRef.current = next;
            return next;
        });
    }

    const clearFilterText = () => {
        setState(prev => {
            const next = { ...prev, filterText: '' };
            stateRef.current = next;
            return next;
        });
    }

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : null;
            const nextDir = current && current.field === field ? (current.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            return { ...prev, sort: [{ field, dir: nextDir }] };
        });
    }, []);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setState(prev => {
            const nextFilterTexts = { ...prev.filterTexts, [field]: val };
            const next = { ...prev, filterTexts: nextFilterTexts };
            stateRef.current = next;
            return next;
        });
    }, []);

    const buildHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `packlist_header_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = () => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field)
                    ? (stateRef.current.sort[0].dir as string)
                    : undefined;

                return (
                    <div className="tmi-header-filter">
                        <div className="tmi-header-filter-content">
                            <div
                                className="tmi-header-filter-label"
                                onClick={() => toggleSort(field)}
                                style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: '4px', lineHeight: '1.1' }}
                            >
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </div>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                                onClick={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </div>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort, handleHeaderFocus, handleHeaderBlur, handleHeaderFilterChange]);

    const handleSplPageChange = (value: SplPage | null) => {
        setState(prev => ({ ...prev, selectedSplPage: value }));
        // Pass the new SPL page ID directly to fetchData to avoid stale state
        fetchData(value ? value.value : null);
    }

    const handleEnterEdit = (dataItem: any) => {
        const original = state.data.find(d => d.packlistID === dataItem.packlistID);
        setState(prev => ({ ...prev, editItemId: dataItem.packlistID, originalItem: original ? { ...original } : null }));
    }

    const handleItemChange = (e: TreeListItemChangeEvent) => {
        const { field, value, dataItem } = e;
        if (!field) return;
        setState(prev => ({ ...prev, data: prev.data.map(item => item.packlistID === dataItem.packlistID ? { ...item, [field]: value } : item) }));
    }

    const handleSave = (dataItem: any) => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePacklist';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                packlistID: Number(dataItem.packlistID),
                packlistCode: dataItem.packlistCode,
                packlistDesc: dataItem.packlistDesc,
                packlistTypeID: Number(dataItem.packlistTypeID),
                packlistStatusID: Number(dataItem.packlistStatusID),
                parentPacklistID: dataItem.parentPacklistID,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Error saving packlist: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, editItemId: null, originalItem: null, errorMsg: '' }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error saving packlist: ' + err.toString() }));
            });
    }

    const handleCancel = () => {
        const { originalItem } = state;
        setState(prev => ({ ...prev, data: originalItem ? prev.data.map(item => item.packlistID === originalItem.packlistID ? originalItem : item) : prev.data, editItemId: null, originalItem: null }));
    }

    const handleSelectionChange = (e: TreeListSelectionChangeEvent) => {
        const id: number = e.dataItem[DATA_ITEM_KEY];
        setState(prev => {
            const next = new Set(prev.selectedIds);
            if (next.has(id)) next.delete(id); else next.add(id);
            return { ...prev, selectedIds: next };
        });
    }

    const handleHeaderSelectionChange = (e: TreeListHeaderSelectionChangeEvent) => {
        const allIds = e.dataItems.map((item: any) => item[DATA_ITEM_KEY] as number);
        const allSelected = allIds.every(id => state.selectedIds.has(id));
        setState(prev => ({ ...prev, selectedIds: allSelected ? new Set() : new Set(allIds) }));
    }

    const openRowMenu = (dataItem: PacklistRow, anchor: HTMLElement) => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
        // Debug: log anchor and bounding rect to help diagnose positioning issues
        // (debug logs removed)
        // We'll measure the real menu before showing to avoid a visual jump.
        // Snapshot anchor bounding rect now (stabilizes values for measurement) and
        // set menuPosition to null to indicate "hidden-for-measure" state.
        try {
            const ar = anchor.getBoundingClientRect();
            const anchorRect = { left: Math.round(ar.left), top: Math.round(ar.top), right: Math.round(ar.right), bottom: Math.round(ar.bottom), width: Math.round(ar.width), height: Math.round(ar.height) };
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } catch {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
        // After rendering, log the popup node and positioning to diagnose placement
        // removed debug logging
    }

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null }));
    }

    const openAttributes = () => {
        const { menuDataItem } = state;
        if (!menuDataItem) {
            setState(prev => ({ ...prev, menuAnchor: null, attributesDlgOpen: false }));
            return;
        }
        // open dialog and fetch attributes for the selected packlist
        setState(prev => ({ ...prev, menuAnchor: null, attributesDlgOpen: true, attributesDlgLoading: true, attributesDlgItems: [], attributesDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/tmi/GetAttributesForPacklist';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(menuDataItem.packlistID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, attributesDlgOpen: false, attributesDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, attributesDlgLoading: false, attributesDlgError: 'Error fetching attributes: ' + res.callStatusMessage }));
                } else {
                    // build a map of original values to detect changes and avoid unnecessary saves
                    const results = res.results ?? [];
                    const origMap: { [key: string]: { attributeValue: string; attributeID: number } } = {};
                    (results as PacklistAttributeInfo[]).forEach((r: PacklistAttributeInfo) => {
                        origMap[r.packListAttributeValueID] = { attributeValue: r.attributeValue ?? '', attributeID: r.attributeID };
                    });
                    setState(prev => ({ ...prev, attributesDlgLoading: false, attributesDlgItems: results, attributesDlgOriginalValues: origMap }));

                    // also fetch attribute definitions for dropdown (packlist-specific)
                    const ddUrl = props.userInf.currProject.apiUrl + '/api/tmi/GetPacklistAttributesForDropDown';
                    fetch(ddUrl, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + props.userInf.token,
                        },
                    })
                        .then(r => r.json())
                        .then(ddRes => {
                            if (ddRes.callStatus === 'OK') {
                                setState(prev => ({ ...prev, packlistAttributesForDropDown: ddRes.results }));
                            }
                        })
                        .catch(() => {});
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, attributesDlgLoading: false, attributesDlgError: 'Error fetching attributes: ' + err.toString() }));
            });
    }

    const closeAttributes = () => {
        setState(prev => ({ ...prev, attributesDlgOpen: false }));
    }

    const addNewAttributeRow = () => {
        const tempId = 'tmp_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
        const defaultAttrID = (state.packlistAttributesForDropDown && state.packlistAttributesForDropDown.length > 0) ? Number(state.packlistAttributesForDropDown[0].value) : 0;
        const packlistID = state.menuDataItem?.packlistID ?? null;
        const newItem: PacklistAttributeInfo & { packlistID?: number | null } = {
            packListAttributeValueID: tempId,
            attributeID: defaultAttrID,
            name: '',
            attributeValue: '',
            lastChangedBy: undefined,
            lastChangedOn: null,
        };
        if (packlistID != null) (newItem as any).packlistID = packlistID;
        setState(prev => ({ ...prev, attributesDlgItems: [newItem as any, ...prev.attributesDlgItems], attributesDlgOriginalValues: { ...prev.attributesDlgOriginalValues, [tempId]: { attributeValue: '', attributeID: 0 } } }));
        // focus the new row's value input shortly after render
        setTimeout(() => {
            try { attributeValueRefs.current[tempId]?.focus(); } catch {}
        }, 50);
    }

    const openPacklistItems = () => {
        const { menuDataItem } = state;
        if (!menuDataItem) {
            setState(prev => ({ ...prev, menuAnchor: null, packlistItemsDlgOpen: false }));
            return;
        }
        setState(prev => ({ ...prev, menuAnchor: null, packlistItemsDlgOpen: true, packlistItemsDlgLoading: true, packlistItemsDlgItems: [], packlistItemsDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemsForPacklists';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify([menuDataItem.packlistID]),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, packlistItemsDlgOpen: false, packlistItemsDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, packlistItemsDlgLoading: false, packlistItemsDlgError: 'Error fetching items: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, packlistItemsDlgLoading: false, packlistItemsDlgItems: res.results }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, packlistItemsDlgLoading: false, packlistItemsDlgError: 'Error fetching items: ' + err.toString() }));
            });
    }

    const closePacklistItems = () => {
        setState(prev => ({ ...prev, packlistItemsDlgOpen: false }));
    }

    const openPLIAttributesForItem = (packlistItemID: string) => {
        if (!packlistItemID) return;
        setState(prev => ({ ...prev, packlistItemAttributesDlgOpen: true, packlistItemAttributesDlgLoading: true, packlistItemAttributesDlgItems: [], packlistItemAttributesDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/tmi/GetPLIAttributesForPLI';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(packlistItemID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, packlistItemAttributesDlgOpen: false, packlistItemAttributesDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgError: 'Error fetching attributes: ' + res.callStatusMessage }));
                } else {
                    const raw = res.results ?? [];
                    const mapped = (raw as any[]).map(r => ({
                        packListAttributeValueID: String(r.packListItemAttributeID ?? r.PackListItemAttributeID ?? r.packListAttributeValueID ?? r.PackListAttributeValueID ?? r.PackListAttributeValueID ?? ''),
                        attributeID: r.attributeID ?? r.AttributeID ?? 0,
                        name: r.name ?? r.Name ?? '',
                        attributeValue: r.packlistItemAttributeValue ?? r.packlistItemAttributeValue ?? r.PacklistItemAttributeValue ?? r.PackListAttributeValue ?? r.packListAttributeValue ?? '',
                        lastChangedBy: r.lastChangedBy ?? r.LastChangedBy ?? '',
                        lastChangedOn: r.lastChangedOn ?? r.LastChangedOn ?? null,
                    } as PacklistAttributeInfo));
                    setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgItems: mapped }));
                    // ensure attribute dropdown defs are available for display mapping
                    if (!state.attributesForDropDown || state.attributesForDropDown.length === 0) {
                        const ddUrl = props.userInf.currProject.apiUrl + '/api/tmi/GetAttributesForDropDown';
                        fetch(ddUrl, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': 'Bearer ' + props.userInf.token,
                            },
                        })
                            .then(r => r.json())
                            .then(ddRes => {
                                if (ddRes.callStatus === 'OK') {
                                    setState(prev => ({ ...prev, attributesForDropDown: ddRes.results }));
                                }
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgError: 'Error fetching attributes: ' + err.toString() }));
            });
    }

    const closePLIAttributesDlg = () => {
        setState(prev => ({ ...prev, packlistItemAttributesDlgOpen: false }));
    }


    // Use refs for mouse handlers so they stay stable across renders.
    const onColMouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
    const onColMouseUpRef = useRef<((e: MouseEvent) => void) | null>(null);

    useEffect(() => {
        onColMouseMoveRef.current = (e: MouseEvent) => {
            if (!colResizeInfoRef.current) return;
            const { dialog, colKey, startX, startWidth } = colResizeInfoRef.current!;
            const dx = e.clientX - startX;
            const newWidth = Math.max(40, Math.round(startWidth + dx));
            if (dialog === 'packlist') {
                setState(prev => ({ ...prev, packlistItemsDlgColWidths: { ...prev.packlistItemsDlgColWidths, [colKey]: newWidth } }));
            } else if (dialog === 'move') {
                setState(prev => ({ ...prev, moveItemsDlgColWidths: { ...prev.moveItemsDlgColWidths, [colKey]: newWidth } }));
            } else if (dialog === 'attributes') {
                setState(prev => ({ ...prev, attributesDlgColWidths: { ...prev.attributesDlgColWidths, [colKey]: newWidth } }));
            }
        };
        onColMouseUpRef.current = (_e: MouseEvent) => {
            // remove listeners and clear ref info
            try {
                document.removeEventListener('mousemove', onColMouseMoveRef.current as EventListener);
                document.removeEventListener('mouseup', onColMouseUpRef.current as EventListener);
            } catch {}
            colResizeInfoRef.current = null;
        };
        return () => {
            // cleanup
            try {
                document.removeEventListener('mousemove', onColMouseMoveRef.current as EventListener);
                document.removeEventListener('mouseup', onColMouseUpRef.current as EventListener);
            } catch {}
        };
    }, []);

    const removeColResizeListeners = () => {
        try {
            document.removeEventListener('mousemove', onColMouseMoveRef.current as EventListener);
            document.removeEventListener('mouseup', onColMouseUpRef.current as EventListener);
        } catch {}
    };

    const startColResize = (dialog: 'packlist' | 'move' | 'attributes', colKey: string, e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = dialog === 'packlist'
            ? (state.packlistItemsDlgColWidths[colKey] ?? 100)
            : dialog === 'move'
                ? (state.moveItemsDlgColWidths[colKey] ?? 100)
                : (state.attributesDlgColWidths[colKey] ?? 100);
        colResizeInfoRef.current = { dialog, colKey, startX, startWidth };
        document.addEventListener('mousemove', onColMouseMoveRef.current as EventListener);
        document.addEventListener('mouseup', onColMouseUpRef.current as EventListener);
    }

    const handlePacklistItemQtyChange = (packlistItemID: string, value: number) => {
        setState(prev => ({ ...prev, packlistItemsDlgItems: prev.packlistItemsDlgItems.map(i => i.packlistItemID === packlistItemID ? { ...i, quantity: value } : i) }));
    }

    const commitPacklistItemQty = (packlistItemID: string) => {
        const item = state.packlistItemsDlgItems.find(i => i.packlistItemID === packlistItemID);
        if (!item) return;
        const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdatePacklistItemQuantity';
        // mark as saving and clear previous error for this item
        setState(prev => ({ ...prev, packlistItemsDlgSavingIds: new Set(prev.packlistItemsDlgSavingIds).add(packlistItemID), packlistItemsDlgItemErrors: { ...prev.packlistItemsDlgItemErrors, [packlistItemID]: '' } }));

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({ packlistItemID: item.packlistItemID, quantity: item.quantity }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => {
                        const next = new Set(prev.packlistItemsDlgSavingIds);
                        next.delete(packlistItemID);
                        return { ...prev, packlistItemsDlgSavingIds: next };
                    });
                } else if (res.callStatus !== 'OK') {
                    setState(prev => {
                        const next = new Set(prev.packlistItemsDlgSavingIds);
                        next.delete(packlistItemID);
                        return { ...prev, packlistItemsDlgSavingIds: next, packlistItemsDlgItemErrors: { ...prev.packlistItemsDlgItemErrors, [packlistItemID]: 'Save failed: ' + res.callStatusMessage } };
                    });
                } else {
                    // If backend returns the updated item, merge it into local state so
                    // LastChangedBy / LastChangedOn (and any other fields) reflect server-side changes.
                    const updatedItem = res.result ?? (Array.isArray(res.results) ? res.results[0] : (res.updatedItem ?? null));
                    setState(prev => {
                        const next = new Set(prev.packlistItemsDlgSavingIds);
                        next.delete(packlistItemID);
                        const items = prev.packlistItemsDlgItems.map(i => i.packlistItemID === packlistItemID
                            ? (updatedItem ? { ...i, ...updatedItem } : i)
                            : i
                        );
                        return { ...prev, packlistItemsDlgSavingIds: next, packlistItemsDlgItems: items };
                    });
                }
            })
            .catch(err => {
                setState(prev => {
                    const next = new Set(prev.packlistItemsDlgSavingIds);
                    next.delete(packlistItemID);
                    return { ...prev, packlistItemsDlgSavingIds: next, packlistItemsDlgItemErrors: { ...prev.packlistItemsDlgItemErrors, [packlistItemID]: 'Save failed: ' + err.toString() } };
                });
            });
    }

    const handleAttributeValueChange = (packListAttributeValueID: string, value: string) => {
        setState(prev => ({ ...prev, attributesDlgItems: prev.attributesDlgItems.map(i => i.packListAttributeValueID === packListAttributeValueID ? { ...i, attributeValue: value } : i) }));
    }

    const handleAttributeIDChange = (packListAttributeValueID: string, attributeID: number) => {
        setState(prev => ({ ...prev, attributesDlgItems: prev.attributesDlgItems.map(i => i.packListAttributeValueID === packListAttributeValueID ? { ...i, attributeID } : i) }));
    }

    const commitAttributeValue = (packListAttributeValueID: string, overrides?: { attributeID?: number; attributeValue?: string }) => {
        const item = state.attributesDlgItems.find(i => i.packListAttributeValueID === packListAttributeValueID);
        if (!item) return;
        const itemToCompare = { ...item, ...(overrides ?? {}) };
        const origEntry = state.attributesDlgOriginalValues?.[packListAttributeValueID];
        const origVal = origEntry ? (origEntry.attributeValue ?? '') : '';
        const origAttrID = origEntry ? (origEntry.attributeID ?? 0) : 0;
        if (((itemToCompare.attributeValue ?? '') === origVal) && (itemToCompare.attributeID === origAttrID)) {
            // nothing changed; no API call needed
            return;
        }
        const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdatePacklistAttribute';
        // optimistically set saving state
        setState(prev => ({ ...prev, attributesDlgSavingIds: new Set(prev.attributesDlgSavingIds).add(packListAttributeValueID), attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: '' }, attributesDlgItems: prev.attributesDlgItems.map(i => i.packListAttributeValueID === packListAttributeValueID ? { ...i, ...(overrides ?? {}) } : i) }));

        const bodyItem = { ...item, ...(overrides ?? {}) } as any;
        // ensure packlistID is included for creating new records
        if (!bodyItem.packlistID) {
            bodyItem.packlistID = state.menuDataItem?.packlistID ?? null;
        }
        // If this is a temporary/new row (temp id), send zero GUID so backend will create
        const ZERO_GUID = '00000000-0000-0000-0000-000000000000';
        if (!bodyItem.packListAttributeValueID || String(bodyItem.packListAttributeValueID).startsWith('tmp_')) {
            bodyItem.packListAttributeValueID = ZERO_GUID;
        }
        console.log('Packlists: committing attribute', packListAttributeValueID, bodyItem);

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(bodyItem),
        })
            .then(resp => resp.json())
            .then(res => {
                console.log('Packlists: UpdatePacklistAttribute response', res);
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, attributesDlgOpen: false, attributesDlgLoading: false, attributesDlgSavingIds: next };
                    });
                } else if (res.callStatus !== 'OK') {
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, attributesDlgSavingIds: next, attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: 'Save failed: ' + res.callStatusMessage } };
                    });
                } else {
                    const updatedItem = res.result ?? (Array.isArray(res.results) ? res.results[0] : (res.updatedItem ?? null));
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        const items = prev.attributesDlgItems.map(i => i.packListAttributeValueID === packListAttributeValueID ? (updatedItem ? { ...i, ...updatedItem } : i) : i);
                        // update original value map so subsequent blurs don't re-save unchanged value
                        const newAttrVal = (updatedItem && updatedItem.attributeValue != null) ? updatedItem.attributeValue : item.attributeValue;
                        const newAttrID = (updatedItem && updatedItem.attributeID != null) ? updatedItem.attributeID : item.attributeID;
                        const origObj = { attributeValue: newAttrVal ?? '', attributeID: newAttrID ?? 0 };
                        return { ...prev, attributesDlgSavingIds: next, attributesDlgItems: items, attributesDlgOriginalValues: { ...prev.attributesDlgOriginalValues, [packListAttributeValueID]: origObj } };
                    });
                }
            })
            .catch(err => {
                setState(prev => {
                    const next = new Set(prev.attributesDlgSavingIds);
                    next.delete(packListAttributeValueID);
                    return { ...prev, attributesDlgSavingIds: next, attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: 'Save failed: ' + err.toString() } };
                });
            });
    }

    const handlePLIAttributeValueChange = (packListItemAttributeID: string, value: string) => {
        setState(prev => ({ ...prev, packlistItemAttributesDlgItems: prev.packlistItemAttributesDlgItems.map(i => i.packListAttributeValueID === packListItemAttributeID ? { ...i, attributeValue: value } : i) }));
    }

    const handlePLIAttributeIDChange = (packListItemAttributeID: string, attributeID: number) => {
        setState(prev => ({ ...prev, packlistItemAttributesDlgItems: prev.packlistItemAttributesDlgItems.map(i => i.packListAttributeValueID === packListItemAttributeID ? { ...i, attributeID } : i) }));
    }

    const commitPLIAttributeValue = (packListItemAttributeID: string, overrides?: { attributeID?: number; attributeValue?: string }) => {
        const item = state.packlistItemAttributesDlgItems.find(i => i.packListAttributeValueID === packListItemAttributeID);
        if (!item) return;
        const itemToCompare = { ...item, ...(overrides ?? {}) } as any;
        const origEntry = state.packlistItemAttributesDlgOriginalValues?.[packListItemAttributeID];
        const origVal = origEntry ? (origEntry.attributeValue ?? '') : '';
        const origAttrID = origEntry ? (origEntry.attributeID ?? 0) : 0;
        if (((itemToCompare.attributeValue ?? '') === origVal) && (itemToCompare.attributeID === origAttrID)) {
            return;
        }

        const url = props.userInf.currProject.apiUrl + '/api/TMI/UpdatePacklistItemAttribute';
        setState(prev => ({ ...prev, packlistItemAttributesDlgSavingIds: new Set(prev.packlistItemAttributesDlgSavingIds).add(packListItemAttributeID), packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListItemAttributeID]: '' }, packlistItemAttributesDlgItems: prev.packlistItemAttributesDlgItems.map(i => i.packListAttributeValueID === packListItemAttributeID ? { ...i, ...(overrides ?? {}) } : i) }));

        const bodyItem: any = { ...item, ...(overrides ?? {}) };
        // backend expects numeric PackListItemAttributeID; if temp id, send 0 for create
        if (!bodyItem.packListAttributeValueID || String(bodyItem.packListAttributeValueID).startsWith('tmp_')) {
            bodyItem.packListItemAttributeID = 0;
        } else {
            bodyItem.packListItemAttributeID = Number(bodyItem.packListAttributeValueID) || 0;
        }
        // ensure packlistItemID is present
        if (!bodyItem.packListItemID) bodyItem.packListItemID = undefined;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(bodyItem),
        })
            .then(r => r.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListItemAttributeID);
                        return { ...prev, packlistItemAttributesDlgOpen: false, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgSavingIds: next };
                    });
                } else if (res.callStatus !== 'OK') {
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListItemAttributeID);
                        return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListItemAttributeID]: 'Save failed: ' + res.callStatusMessage } };
                    });
                } else {
                    const updated = res.result ?? (Array.isArray(res.results) ? res.results[0] : null);
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListItemAttributeID);
                        const items = prev.packlistItemAttributesDlgItems.map(i => i.packListAttributeValueID === packListItemAttributeID ? (updated ? { ...i, ...updated } : i) : i);
                        const newVal = (updated && (updated.packlistItemAttributeValue ?? updated.PacklistItemAttributeValue ?? updated.packListAttributeValue)) ?? item.attributeValue;
                        const newAttrID = (updated && (updated.attributeID ?? updated.AttributeID)) ?? item.attributeID;
                        const origObj = { attributeValue: newVal ?? '', attributeID: newAttrID ?? 0 };
                        return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItems: items, packlistItemAttributesDlgOriginalValues: { ...prev.packlistItemAttributesDlgOriginalValues, [packListItemAttributeID]: origObj } };
                    });
                }
            })
            .catch(err => {
                setState(prev => {
                    const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                    next.delete(packListItemAttributeID);
                    return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListItemAttributeID]: 'Save failed: ' + err.toString() } };
                });
            });
    }

    const deleteAttribute = (packListAttributeValueID: string) => {
        const item = state.attributesDlgItems.find(i => i.packListAttributeValueID === packListAttributeValueID);
        if (!item) return;
        // If temporary row, remove locally
        if (String(item.packListAttributeValueID).startsWith('tmp_')) {
            setState(prev => {
                const items = prev.attributesDlgItems.filter(i => i.packListAttributeValueID !== packListAttributeValueID);
                const orig = { ...prev.attributesDlgOriginalValues } as any;
                delete orig[packListAttributeValueID];
                try { attributeValueRefs.current[packListAttributeValueID] = null; } catch {}
                return { ...prev, attributesDlgItems: items, attributesDlgOriginalValues: orig };
            });
            return;
        }

        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistAttributeValue';
        setState(prev => ({ ...prev, attributesDlgSavingIds: new Set(prev.attributesDlgSavingIds).add(packListAttributeValueID), attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: '' } }));
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({ packListAttributeValueID }),
        })
            .then(r => r.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, attributesDlgOpen: false, attributesDlgLoading: false, attributesDlgSavingIds: next };
                    });
                } else if (res.callStatus !== 'OK') {
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, attributesDlgSavingIds: next, attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: 'Delete failed: ' + res.callStatusMessage } };
                    });
                } else {
                    setState(prev => {
                        const next = new Set(prev.attributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        const items = prev.attributesDlgItems.filter(i => i.packListAttributeValueID !== packListAttributeValueID);
                        const orig = { ...prev.attributesDlgOriginalValues } as any;
                        delete orig[packListAttributeValueID];
                        try { attributeValueRefs.current[packListAttributeValueID] = null; } catch {}
                        return { ...prev, attributesDlgSavingIds: next, attributesDlgItems: items, attributesDlgOriginalValues: orig };
                    });
                }
            })
            .catch(err => {
                setState(prev => {
                    const next = new Set(prev.attributesDlgSavingIds);
                    next.delete(packListAttributeValueID);
                    return { ...prev, attributesDlgSavingIds: next, attributesDlgItemErrors: { ...prev.attributesDlgItemErrors, [packListAttributeValueID]: 'Delete failed: ' + err.toString() } };
                });
            });
    }

    const openAttributesDeleteConfirm = (packListAttributeValueID: string) => {
        setState(prev => ({
            ...prev,
            attributesDeleteConfirmId: packListAttributeValueID,
            attributesDeleteInProgress: false,
            attributesDeleteError: '',
        }));
    };

    const closeAttributesDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            attributesDeleteConfirmId: null,
            attributesDeleteInProgress: false,
            attributesDeleteError: '',
        }));
    };

    const confirmAttributesDelete = () => {
        if (!state.attributesDeleteConfirmId) return;
        const idToDelete = state.attributesDeleteConfirmId;
        setState(prev => ({ ...prev, attributesDeleteInProgress: true, attributesDeleteError: '' }));

        deleteAttribute(idToDelete);
        setState(prev => ({
            ...prev,
            attributesDeleteConfirmId: null,
            attributesDeleteInProgress: false,
            attributesDeleteError: '',
        }));
    };

    const deletePacklistItemAttribute = (packListAttributeValueID: string) => {
        if (String(packListAttributeValueID).startsWith('tmp_')) {
            setState(prev => ({ ...prev, packlistItemAttributesDlgItems: prev.packlistItemAttributesDlgItems.filter(i => i.packListAttributeValueID !== packListAttributeValueID) }));
            return;
        }

        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklistItemAttribute';
        setState(prev => ({ ...prev, packlistItemAttributesDlgSavingIds: new Set(prev.packlistItemAttributesDlgSavingIds).add(packListAttributeValueID), packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListAttributeValueID]: '' } }));
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({ packListItemAttributeID: Number(packListAttributeValueID) }),
        })
            .then(r => r.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, packlistItemAttributesDlgOpen: false, packlistItemAttributesDlgLoading: false, packlistItemAttributesDlgSavingIds: next };
                    });
                } else if (res.callStatus !== 'OK') {
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListAttributeValueID]: 'Delete failed: ' + res.callStatusMessage } };
                    });
                } else {
                    setState(prev => {
                        const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                        next.delete(packListAttributeValueID);
                        const items = prev.packlistItemAttributesDlgItems.filter(i => i.packListAttributeValueID !== packListAttributeValueID);
                        const orig = { ...prev.packlistItemAttributesDlgOriginalValues } as any;
                        delete orig[packListAttributeValueID];
                        return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItems: items, packlistItemAttributesDlgOriginalValues: orig };
                    });
                }
            })
            .catch(err => {
                setState(prev => {
                    const next = new Set(prev.packlistItemAttributesDlgSavingIds);
                    next.delete(packListAttributeValueID);
                    return { ...prev, packlistItemAttributesDlgSavingIds: next, packlistItemAttributesDlgItemErrors: { ...prev.packlistItemAttributesDlgItemErrors, [packListAttributeValueID]: 'Delete failed: ' + err.toString() } };
                });
            });
    };

    const openPacklistItemAttributesDeleteConfirm = (packListAttributeValueID: string) => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesDeleteConfirmId: packListAttributeValueID,
            packlistItemAttributesDeleteInProgress: false,
            packlistItemAttributesDeleteError: '',
        }));
    };

    const closePacklistItemAttributesDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            packlistItemAttributesDeleteConfirmId: null,
            packlistItemAttributesDeleteInProgress: false,
            packlistItemAttributesDeleteError: '',
        }));
    };

    const confirmPacklistItemAttributesDelete = () => {
        if (!state.packlistItemAttributesDeleteConfirmId) return;
        const idToDelete = state.packlistItemAttributesDeleteConfirmId;
        setState(prev => ({ ...prev, packlistItemAttributesDeleteInProgress: true, packlistItemAttributesDeleteError: '' }));

        deletePacklistItemAttribute(idToDelete);
        setState(prev => ({
            ...prev,
            packlistItemAttributesDeleteConfirmId: null,
            packlistItemAttributesDeleteInProgress: false,
            packlistItemAttributesDeleteError: '',
        }));
    };

    const handleAttributesSortChange = (field: string) => {
        setState(prev => {
            const cur = prev.attributesDlgSort && prev.attributesDlgSort.length > 0 ? prev.attributesDlgSort[0] : null;
            let next: SortDescriptor[];
            if (cur && cur.field === field) {
                next = [{ field, dir: cur.dir === 'asc' ? 'desc' : 'asc' }];
            } else {
                next = [{ field, dir: 'asc' }];
            }
            console.log('Packlists: attributes sort change', next);
            return { ...prev, attributesDlgSort: next };
        });
    }

    // prepare items for rendering: include attribute display text and remap sort descriptor
    const attributesRenderItems = (() => {
        const mapped = state.attributesDlgItems.map(i => ({
            ...i as any,
            attributeID_display: (state.packlistAttributesForDropDown.find(d => String(d.value) === String(i.attributeID))?.display ?? String(i.attributeID)),
        }));
        const sortForOrder = (state.attributesDlgSort ?? []).map(sd => sd.field === 'attributeID' ? { ...sd, field: 'attributeID_display' } : sd);
        return orderBy(mapped, sortForOrder);
    })();

    const onMenuFocus = () => {
        if (menuBlurTimeoutRef.current) clearTimeout(menuBlurTimeoutRef.current);
    }

    const onMenuBlur = () => {
        menuBlurTimeoutRef.current = setTimeout(() => closeRowMenu(), 100);
    }

    // Close menu when clicking outside the menu or anchor button
    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Node;
            // Check if click is outside the menu ref and outside the anchor button
            if (menuRef.current && !menuRef.current.contains(target) &&
                state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        if (state.menuDataItem) {
            document.addEventListener('click', handleDocumentClick);
            return () => document.removeEventListener('click', handleDocumentClick);
        }
    }, [state.menuDataItem, state.menuAnchor]);

    // When menuAnchor exists but menuPosition is null, render the menu hidden
    // and measure its real size, then compute the final position and show it.
    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                // Clamp horizontally
                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                // If not enough room below, flip above and anchor bottom-left to the button
                if (top + menuH > scrollY + winH) {
                    // Account for the anchor (button) height when flipping above so the
                    // menu bottom aligns with the top of the button.
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    left = Math.round(actualAnchorRect.left + scrollX);
                    if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (left < scrollX + 8) left = Math.round(scrollX + 8);
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch (e) {
                // ignore measurement errors
            }
        }, 10);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    // Run measurement/flip when an anchor and a menuPosition exist (we may adjust based on real menu size)
    useEffect(() => {
        if (!state.menuAnchor || !state.menuPosition) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const snap = state.menuAnchorRect;
                const anchorRect = snap ? snap as any : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const menuW = menuRect.width;
                const menuH = menuRect.height;

                const belowTop = Math.round(anchorRect.bottom + scrollY);
                const anchorLeftAbs = Math.round(anchorRect.left + scrollX);

                let newLeft = state.menuPosition!.left;
                let newTop = state.menuPosition!.top;

                // If currently positioned below the anchor (top at/after belowTop) but it overflows the viewport bottom, flip above
                if (state.menuPosition!.top >= belowTop - 2 && (menuRect.bottom > winH)) {
                    // Anchor bottom-left should align with menu bottom-left: left = anchor.left
                    newLeft = anchorLeftAbs;
                    // Compute flipped top so menu bottom aligns with the top of the button
                    // (account for the button height so menu doesn't render too low).
                    newTop = Math.round(anchorRect.top + scrollY - menuH + MENU_FLIP_OFFSET);
                    // Clamp horizontally if necessary
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                } else {
                    // Otherwise, ensure menu is clamped within viewport horizontally/vertically
                    if (newLeft + menuW > scrollX + winW) newLeft = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                    if (newLeft < scrollX + 8) newLeft = Math.round(scrollX + 8);
                    if (newTop + menuH > scrollY + winH) newTop = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                    if (newTop < scrollY + 8) newTop = Math.round(scrollY + 8);
                }

                // Debugging info removed

                if (newLeft !== state.menuPosition!.left || newTop !== state.menuPosition!.top) {
                    setState(prev => ({ ...prev, menuPosition: { left: newLeft, top: newTop } }));
                }
            } catch (e) {
                // ignore measurement errors
            }
        }, 30);
        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    const handleMoveSelectedUnder = () => {
        const { menuDataItem, selectedIds } = state;
        if (!menuDataItem || selectedIds.size === 0) {
            closeRowMenu();
            return;
        }
        const url = props.userInf.currProject.apiUrl + '/api/TMI/MovePacklistsUnder';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                checked: Array.from(selectedIds),
                selected: menuDataItem.packlistID,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, errorMsg: 'Move failed: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, selectedIds: new Set() }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Move failed: ' + err.toString() }));
            })
            .finally(() => closeRowMenu());
    }

    const handleAddSelectedAsChildren = () => {
        const { menuDataItem, selectedIds, data } = state;
        if (!menuDataItem || selectedIds.size === 0) {
            closeRowMenu();
            return;
        }
        closeRowMenu();
        const selectedRows = data.filter(r => selectedIds.has(r.packlistID));
        setState(prev => ({ ...prev, addChildrenDlgOpen: true, addChildrenDlgLoading: false, addChildrenDlgError: '', addChildrenDlgDest: menuDataItem, addChildrenDlgItems: selectedRows, addChildrenDlgSelectedIds: new Set(selectedRows.map(r => r.packlistID)) }));
    }

    const closeAddChildrenDlg = () => {
        setState(prev => ({ ...prev, addChildrenDlgOpen: false, addChildrenDlgItems: [], addChildrenDlgSelectedIds: new Set(), addChildrenDlgDest: null, addChildrenDlgError: '' }));
    }

    const toggleAddChildrenDlgItem = (packlistID: number) => {
        setState(prev => {
            const next = new Set(prev.addChildrenDlgSelectedIds);
            if (next.has(packlistID)) next.delete(packlistID); else next.add(packlistID);
            return { ...prev, addChildrenDlgSelectedIds: next };
        });
    }

    const toggleAllAddChildrenDlgItems = () => {
        const { addChildrenDlgItems, addChildrenDlgSelectedIds } = state;
        const allSelected = addChildrenDlgItems.every(i => addChildrenDlgSelectedIds.has(i.packlistID));
        setState(prev => ({ ...prev, addChildrenDlgSelectedIds: allSelected ? new Set() : new Set(addChildrenDlgItems.map(i => i.packlistID)) }));
    }

    const confirmAddChildren = () => {
        const { addChildrenDlgDest, addChildrenDlgSelectedIds } = state;
        if (!addChildrenDlgDest || addChildrenDlgSelectedIds.size === 0) return;
        setState(prev => ({ ...prev, addChildrenDlgLoading: true, addChildrenDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddPacklistsAsChildren';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                checked: Array.from(addChildrenDlgSelectedIds),
                selected: addChildrenDlgDest.packlistID,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, addChildrenDlgOpen: false, addChildrenDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, addChildrenDlgLoading: false, addChildrenDlgError: 'Failed: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, addChildrenDlgOpen: false, addChildrenDlgLoading: false, addChildrenDlgItems: [], addChildrenDlgSelectedIds: new Set(), addChildrenDlgDest: null, selectedIds: new Set() }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, addChildrenDlgLoading: false, addChildrenDlgError: 'Failed: ' + err.toString() }));
            });
    }

    const handleMoveItemsToSelected = () => {
        const { menuDataItem, selectedIds } = state;
        if (!menuDataItem || selectedIds.size === 0) {
            closeRowMenu();
            return;
        }
        closeRowMenu();
        setState(prev => ({ ...prev, moveItemsDlgOpen: true, moveItemsDlgLoading: true, moveItemsDlgError: '', moveItemsDlgItems: [], moveItemsDlgDest: menuDataItem }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetItemsForPacklists';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(Array.from(selectedIds)),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, moveItemsDlgOpen: false, moveItemsDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, moveItemsDlgLoading: false, moveItemsDlgError: 'Error fetching items: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, moveItemsDlgLoading: false, moveItemsDlgItems: res.results, moveItemsDlgSelectedIds: new Set((res.results as PacklistItem[]).map(i => i.packlistItemID)) }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, moveItemsDlgLoading: false, moveItemsDlgError: 'Error fetching items: ' + err.toString() }));
            });
    }

    const closeMoveItemsDlg = () => {
        setState(prev => ({ ...prev, moveItemsDlgOpen: false, moveItemsDlgItems: [], moveItemsDlgSelectedIds: new Set(), moveItemsDlgDest: null, moveItemsDlgError: '' }));
    }

    const toggleMoveItemsDlgItem = (packlistItemID: string) => {
        setState(prev => {
            const next = new Set(prev.moveItemsDlgSelectedIds);
            if (next.has(packlistItemID)) next.delete(packlistItemID); else next.add(packlistItemID);
            return { ...prev, moveItemsDlgSelectedIds: next };
        });
    }



    const toggleAllMoveItemsDlgItems = () => {
        const { moveItemsDlgItems, moveItemsDlgSelectedIds } = state;
        const allSelected = moveItemsDlgItems.every(i => moveItemsDlgSelectedIds.has(i.packlistItemID));
        setState(prev => ({ ...prev, moveItemsDlgSelectedIds: allSelected ? new Set() : new Set(moveItemsDlgItems.map(i => i.packlistItemID)) }));
    }

    const confirmMoveItems = () => {
        const { moveItemsDlgDest, moveItemsDlgSelectedIds, moveItemsDlgItems } = state;
        if (!moveItemsDlgDest || moveItemsDlgSelectedIds.size === 0) return;
        setState(prev => ({ ...prev, moveItemsDlgLoading: true, moveItemsDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/MoveItemsToPacklist';
        const checkedItems = moveItemsDlgItems
            .filter(i => moveItemsDlgSelectedIds.has(i.packlistItemID))
            .map(i => ({ id: i.packlistItemID, qty: i.quantity }));
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                selected: moveItemsDlgDest.packlistID,
                checked: checkedItems,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, moveItemsDlgOpen: false, moveItemsDlgLoading: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, moveItemsDlgLoading: false, moveItemsDlgError: 'Move failed: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, moveItemsDlgOpen: false, moveItemsDlgLoading: false, moveItemsDlgItems: [], moveItemsDlgSelectedIds: new Set(), moveItemsDlgDest: null, selectedIds: new Set() }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, moveItemsDlgLoading: false, moveItemsDlgError: 'Move failed: ' + err.toString() }));
            });
    }


    const openDeleteConfirm = (dataItem: PacklistRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: dataItem, deleteErrorMsg: '' }));
    }

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
    }
    const confirmDelete = () => {
        const { deleteConfirmItem } = state;
        if (!deleteConfirmItem) return;
        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeletePacklist';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(deleteConfirmItem.packlistID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeDeleteConfirm();
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + res.callStatusMessage }));
                } else {
                    closeDeleteConfirm();
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, deleteInProgress: false, deleteErrorMsg: 'Delete failed: ' + err.toString() }));
            });
    }

    const openNewPacklistDlg = () => {
        const selectedParentID = state.selectedIds.size > 0 ? Array.from(state.selectedIds)[0] : null;
        setState(prev => ({ ...prev, newPacklistDlgOpen: true, newPacklistDlgSaving: false, newPacklistDlgError: '', newPacklistDlgCode: '', newPacklistDlgDesc: '', newPacklistDlgTypeID: state.packlistTypes[0]?.value ?? '', newPacklistDlgParentID: selectedParentID }));
    }

    const closeNewPacklistDlg = () => {
        setState(prev => ({ ...prev, newPacklistDlgOpen: false }));
    }

    const submitNewPacklist = () => {
        const { newPacklistDlgCode, newPacklistDlgDesc, newPacklistDlgTypeID, newPacklistDlgParentID } = state;
        setState(prev => ({ ...prev, newPacklistDlgSaving: true, newPacklistDlgError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdatePacklist';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                packlistCode: newPacklistDlgCode,
                packlistDesc: newPacklistDlgDesc,
                packlistTypeID: Number(newPacklistDlgTypeID),
                parentPacklistID: newPacklistDlgParentID,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newPacklistDlgOpen: false, newPacklistDlgSaving: false }));
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, newPacklistDlgSaving: false, newPacklistDlgError: 'Error creating packlist: ' + res.callStatusMessage }));
                } else {
                    setState(prev => ({ ...prev, newPacklistDlgOpen: false, newPacklistDlgSaving: false }));
                    fetchData();
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newPacklistDlgSaving: false, newPacklistDlgError: 'Error creating packlist: ' + err.toString() }));
            });
    }

    // textCell / expandableTextCell / dateCell are shared, read-only cell renderers used by
    // several columns. They previously were plain `const` functions re-created on every render,
    // which (like the duplicated editable-text-cell renderers fixed in MasterUsers.tsx) are given
    // a new identity each render. They have no closures over outer `state`, so they are memoized
    // with useCallback using an empty dependency array to keep a permanently stable identity.
    const textCell = useCallback((props: TreeListCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const isSelected = props.dataItem[SELECTED_FIELD];
        return <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`} title={String(val)}>{val}</td>;
    }, []);

    const expandableTextCell = useCallback((props: TreeListCellProps) => {
        const { dataItem, level, hasChildren, onExpandChange } = props;
        const val = dataItem[props.field!] ?? '';
        const indent = level.length - 1;
        const isSelected = dataItem[SELECTED_FIELD];
        return (
            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`} title={String(val)}>
                <span className="packlists-cell-indent" style={{ width: indent * 16 }} />
                {hasChildren
                    ? <span
                        className={`k-icon k-font-icon ${dataItem.expanded ? 'k-i-collapse' : 'k-i-expand'} packlists-expand-icon`}
                        onClick={e => onExpandChange(e, dataItem, level)}
                      />
                    : <span className="packlists-expand-spacer" />
                }
                {val}
            </td>
        );
    }, []);

    const dateCell = useCallback((props: TreeListCellProps) => {
        const val = props.dataItem[props.field!];
        const display = val ? new Date(val).toLocaleString() : '';
        const isSelected = props.dataItem[SELECTED_FIELD];
        return <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`} title={display}>{display}</td>;
    }, []);

    // PacklistTypeCell / PacklistStatusCell render a DropDownList while editing and close over
    // state.packlistTypes / state.packlistStatuses respectively. They are memoized with
    // useCallback, depending only on the list they actually read, so their identity stays stable
    // across unrelated re-renders (e.g. every keystroke in another column) and only changes when
    // their own dropdown data actually changes.
    const PacklistTypeCell = useCallback((props: TreeListCellProps) => {
        const { dataItem, field } = props;
        const { packlistTypes } = state;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        const isSelected = dataItem[SELECTED_FIELD];
        const currentId: number = dataItem[field!];
        const selected = packlistTypes.find(t => String(t.value) === String(currentId)) ?? null;

        if (inEdit) {
            return (
                <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                    <DropDownList
                        data={packlistTypes}
                        textField="display"
                        dataItemKey="value"
                        value={selected}
                        onChange={e => {
                            if (props.onChange) {
                                props.onChange({
                                    dataItem,
                                    level: props.level,
                                    syntheticEvent: e.syntheticEvent,
                                    field: field,
                                    value: Number(e.value.value),
                                });
                            }
                        }}
                        className="packlists-dropdown-full-width"
                    />
                </td>
            );
        }
        const displayVal = selected ? selected.display : String(currentId);
        return <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`} title={displayVal}>{displayVal}</td>;
    }, [state.packlistTypes]);

    const PacklistStatusCell = useCallback((props: TreeListCellProps) => {
        const { dataItem, field } = props;
        const { packlistStatuses } = state;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        const isSelected = dataItem[SELECTED_FIELD];
        const currentId: number = dataItem[field!];
        const selected = packlistStatuses.find(t => String(t.value) === String(currentId)) ?? null;

        if (inEdit) {
            return (
                <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                    <DropDownList
                        data={packlistStatuses}
                        textField="display"
                        dataItemKey="value"
                        value={selected}
                        onChange={e => {
                            if (props.onChange) {
                                props.onChange({
                                    dataItem,
                                    level: props.level,
                                    syntheticEvent: e.syntheticEvent,
                                    field: field,
                                    value: Number(e.value.value),
                                });
                            }
                        }}
                        className="packlists-dropdown-full-width"
                    />
                </td>
            );
        }
        const displayVal = selected ? selected.display : String(currentId);
        return <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`} title={displayVal}>{displayVal}</td>;
    }, [state.packlistStatuses]);

    const HamburgerHeaderCell = () => {
        return (
            <div className="tmi-header-action-cell tmi-command-column">
                <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={() => fetchData()} disabled={loading} title="Refresh">
                    <span className="k-icon k-font-icon k-i-reload" />
                </button>
            </div>
        );
    }

    const CommandHeaderCell = () => {
        return (
            <div className="tmi-header-action-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-header-action-button"
                    title="New Packlist"
                    onClick={openNewPacklistDlg}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </div>
        );
    }

    const CommandCell = (props: TreeListCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={handleCancel} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={() => openDeleteConfirm(dataItem as PacklistRow)} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={() => handleEnterEdit(dataItem)} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={() => openDeleteConfirm(dataItem as PacklistRow)} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    }

    const HamburgerCell = (props: TreeListCellProps) => {
        const { dataItem } = props;
        const inEdit: boolean = dataItem[EDIT_FIELD];
        return (
            <td className="packlists-hamburger-cell">
                {inEdit ? (
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-first"
                        title="Save"
                        onClick={() => handleSave(dataItem)}
                    >
                        <span className="k-icon k-font-icon k-i-check" />
                    </button>
                ) : (
                    <button
                        className="k-button k-button-sm k-rounded-md tmi-dialog-action-button tmi-dialog-action-button-first"
                        title="Actions"
                        onClick={e => {
                            e.stopPropagation();
                            openRowMenu(dataItem as PacklistRow, e.currentTarget);
                        }}
                    >
                        <span className="k-icon k-font-icon k-i-more-vertical" />
                    </button>
                )}
            </td>
        );
    }

    const {
        data,
        loading,
        errorMsg,
        expanded,
        sort,
        filter,
        editItemId,
        selectedIds,
        packlistTypes,
        packlistStatuses,
    } = state;

    const columnFilteredData = applyPacklistFilters(data, state.filterTexts, packlistTypes, packlistStatuses);
    const sortedData = orderBy(columnFilteredData, sort);

    const tree = createDataTree(
        sortedData,
        item => item.packlistID,
        item => item.parentPacklistID,
        SUB_ITEMS_FIELD
    );

    const filteredTree = filter.length > 0 ? filterBy(tree, filter, SUB_ITEMS_FIELD) : tree;

    const treeData = mapTree(filteredTree, SUB_ITEMS_FIELD, item =>
        extendDataItem(item, SUB_ITEMS_FIELD, {
            expanded: expanded.includes(item[DATA_ITEM_KEY]),
            [EDIT_FIELD]: item[DATA_ITEM_KEY] === editItemId,
            [SELECTED_FIELD]: selectedIds.has(item[DATA_ITEM_KEY]),
        })
    );

    const columns = useMemo(() => [
        { field: SELECTED_FIELD, width: 50, headerCell: TreeListHeaderSelectionCell, cell: TreeListSelectionCell },
        { headerCell: HamburgerHeaderCell, cell: HamburgerCell, width: 40 },
        { field: 'displayText', title: 'Display Text', headerCell: buildHeaderCellRenderer('displayText', 'Display Text'), expandable: true, cell: expandableTextCell, width: 300 },
        { field: 'packlistID', title: 'ID', headerCell: buildHeaderCellRenderer('packlistID', 'ID'), cell: textCell, width: 80 },
        { field: 'packlistCode', title: 'Code', headerCell: buildHeaderCellRenderer('packlistCode', 'Code'), cell: textCell, editCell: TreeListTextEditor },
        { field: 'packlistDesc', title: 'Description', headerCell: buildHeaderCellRenderer('packlistDesc', 'Description'), cell: textCell, editCell: TreeListTextEditor, width: 300 },
        { field: 'packlistTypeID', title: 'Type', headerCell: buildHeaderCellRenderer('packlistTypeID', 'Type'), cell: PacklistTypeCell, editCell: PacklistTypeCell },
        { field: 'packlistStatusID', title: 'Status', headerCell: buildHeaderCellRenderer('packlistStatusID', 'Status'), cell: PacklistStatusCell, editCell: PacklistStatusCell, width: 80 },
        { field: 'lastChangedBy', title: 'Changed By', headerCell: buildHeaderCellRenderer('lastChangedBy', 'Changed By'), cell: textCell },
        { field: 'lastChangedOn', title: 'Changed On', headerCell: buildHeaderCellRenderer('lastChangedOn', 'Changed On'), cell: dateCell, width: 160 },
        { headerCell: CommandHeaderCell, cell: CommandCell, width: 80, locked: true, resizable: false, headerClassName: 'tmi-command-column' },
    ], [buildHeaderCellRenderer, textCell, expandableTextCell, dateCell, PacklistTypeCell, PacklistStatusCell]);

    return (
        <div className="packlists-container">
            <div className="packlists-header">
                <div className="packlists-header-top">
                    {loading && <span className="k-icon k-font-icon k-i-loading" />}
                </div>
                <div className="packlists-header-filters">
                    <div className="packlists-spl-filter">
                        <span className="packlists-spl-label">SPL Page filter:</span>
                        <DropDownList
                            data={state.splPages}
                            textField="display"
                            dataItemKey="value"
                            value={state.selectedSplPage}
                            onChange={e => handleSplPageChange(e.value)}
                            className="packlists-spl-dropdown"
                        />
                    </div>
                </div>
            </div>
            {errorMsg && <div className="packlists-error">{errorMsg}</div>}
            <TreeList
                className="tmi-tree packlists-treelist"
                data={treeData}
                dataItemKey={DATA_ITEM_KEY}
                subItemsField={SUB_ITEMS_FIELD}
                expandField="expanded"
                onExpandChange={handleExpandChange}
                resizable
                sortable
                sort={sort}
                onSortChange={handleSortChange}
                filter={[]}
                onFilterChange={handleFilterChange}
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
                selectedField={SELECTED_FIELD}
                onSelectionChange={handleSelectionChange}
                onHeaderSelectionChange={handleHeaderSelectionChange}
                columns={columns}
            />
            {state.menuAnchor !== null && (
                <div
                    className="packlists-menu-overlay"
                    onClick={closeRowMenu}
                />
            )}
            {state.menuAnchor && (() => {
                const left = state.menuPosition ? state.menuPosition.left : -9999;
                const top = state.menuPosition ? state.menuPosition.top : -9999;
                const hidden = state.menuPosition === null;
                const menu = (
                    <div ref={menuRef} className="k-popup packlist-row-menu packlists-row-menu" style={{ left, top, visibility: hidden ? 'hidden' : undefined }}>
                        <div
                            tabIndex={-1}
                            onFocus={onMenuFocus}
                            onBlur={onMenuBlur}
                            className="packlists-menu-content"
                        >
                            <button
                                className="k-button k-button-flat"
                                style={{ justifyContent: 'flex-start', padding: '6px 16px' }}
                                onClick={handleMoveSelectedUnder}
                                disabled={state.selectedIds.size === 0}
                            >
                                Move selected Packlists under here
                            </button>
                            <button
                                className="k-button k-button-flat"
                                style={{ justifyContent: 'flex-start', padding: '6px 16px' }}
                                onClick={openAttributes}
                            >
                                Attributes
                            </button>
                            <button
                                className="k-button k-button-flat"
                                style={{ justifyContent: 'flex-start', padding: '6px 16px' }}
                                onClick={openPacklistItems}
                            >
                                Packlist Items
                            </button>
                            <button
                                className="k-button k-button-flat"
                                style={{ justifyContent: 'flex-start', padding: '6px 16px' }}
                                onClick={handleAddSelectedAsChildren}
                                disabled={state.selectedIds.size === 0}
                            >
                                Add selected Packlists as children to this one
                            </button>
                            <button
                                className="k-button k-button-flat"
                                style={{ justifyContent: 'flex-start', padding: '6px 16px' }}
                                onClick={handleMoveItemsToSelected}
                                disabled={state.selectedIds.size === 0}
                            >
                                Move Items from selected Packlists to this one
                            </button>
                        </div>
                    </div>
                );
                return createPortal(menu, document.body);
            })()}

            {state.moveItemsDlgOpen && (
                <Dialog
                    title={`Move Items to: ${state.moveItemsDlgDest?.displayText ?? ''}`}
                    onClose={closeMoveItemsDlg}
                    width={720}
                >
                    {state.moveItemsDlgError && (
                        <div style={{ color: 'red', padding: '8px 0' }}>{state.moveItemsDlgError}</div>
                    )}
                    <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                        <table className="k-table k-table-md packlists-treelist" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <colgroup>
                                <col style={{ width: state.moveItemsDlgColWidths.checkbox + 'px' }} />
                                <col style={{ width: state.moveItemsDlgColWidths.itemDesc + 'px' }} />
                                <col style={{ width: state.moveItemsDlgColWidths.quantity + 'px' }} />
                                <col style={{ width: state.moveItemsDlgColWidths.itemType + 'px' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th style={{ width: state.moveItemsDlgColWidths.checkbox, padding: '6px 8px', borderBottom: '1px solid #ccc', textAlign: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                        <input
                                            type="checkbox"
                                            checked={state.moveItemsDlgItems.length > 0 && state.moveItemsDlgItems.every(i => state.moveItemsDlgSelectedIds.has(i.packlistItemID))}
                                            onChange={toggleAllMoveItemsDlgItems}
                                        />
                                        <div onMouseDown={e => startColResize('move', 'checkbox', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                        Item Description
                                        <div onMouseDown={e => startColResize('move', 'itemDesc', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                    </th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                        Quantity
                                        <div onMouseDown={e => startColResize('move', 'quantity', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                        Item Type
                                        <div onMouseDown={e => startColResize('move', 'itemType', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.moveItemsDlgItems.map(item => (
                                    <tr key={item.packlistItemID} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={state.moveItemsDlgSelectedIds.has(item.packlistItemID)}
                                                onChange={() => toggleMoveItemsDlgItem(item.packlistItemID)}
                                            />
                                        </td>
                                        <td title={String(item.itemDesc)} style={{ padding: '4px 8px' }}>{item.itemDesc}</td>
                                        <td title={String(item.quantity)} style={{ padding: '4px 8px', textAlign: 'right' }}>{item.quantity}</td>
                                        <td title={String(item.itemType)} style={{ padding: '4px 8px' }}>{item.itemType}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeMoveItemsDlg} disabled={state.moveItemsDlgLoading}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={confirmMoveItems}
                            disabled={state.moveItemsDlgLoading || state.moveItemsDlgSelectedIds.size === 0}
                        >
                            {state.moveItemsDlgLoading ? <span className="k-icon k-font-icon k-i-loading" /> : 'Move Items'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.attributesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeAttributesDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this attribute?
                    </p>
                    {state.attributesDeleteError && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.attributesDeleteError}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAttributesDeleteConfirm} disabled={state.attributesDeleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmAttributesDelete}
                            disabled={state.attributesDeleteInProgress}
                        >
                            {state.attributesDeleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.addChildrenDlgOpen && (
                <Dialog
                    title={`Add as children of: ${state.addChildrenDlgDest?.displayText ?? ''}`}
                    onClose={closeAddChildrenDlg}
                    width={500}
                >
                    {state.addChildrenDlgError && (
                        <div style={{ color: 'red', padding: '8px 0' }}>{state.addChildrenDlgError}</div>
                    )}
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        <table className="k-table k-table-md" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 36, padding: '6px 8px', borderBottom: '1px solid #ccc', textAlign: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                        <input
                                            type="checkbox"
                                            checked={state.addChildrenDlgItems.length > 0 && state.addChildrenDlgItems.every(i => state.addChildrenDlgSelectedIds.has(i.packlistID))}
                                            onChange={toggleAllAddChildrenDlgItems}
                                        />
                                    </th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Display Text</th>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.addChildrenDlgItems.map(item => (
                                    <tr key={item.packlistID} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ textAlign: 'center', padding: '4px 8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={state.addChildrenDlgSelectedIds.has(item.packlistID)}
                                                onChange={() => toggleAddChildrenDlgItem(item.packlistID)}
                                            />
                                        </td>
                                        <td style={{ padding: '4px 8px' }}>{item.displayText}</td>
                                        <td style={{ padding: '4px 8px' }}>{item.packlistCode}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAddChildrenDlg} disabled={state.addChildrenDlgLoading}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={confirmAddChildren}
                            disabled={state.addChildrenDlgLoading || state.addChildrenDlgSelectedIds.size === 0}
                        >
                            {state.addChildrenDlgLoading ? <span className="k-icon k-font-icon k-i-loading" /> : 'Confirm'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.attributesDlgOpen && (
                <Dialog
                    title={`Attributes - ${state.menuDataItem?.displayText ?? ''}`}
                    onClose={closeAttributes}
                    width={980}
                >
                    <div style={{ padding: '8px 0' }}>
                        {state.attributesDlgError && (
                            <div style={{ color: 'red', marginBottom: 8 }}>{state.attributesDlgError}</div>
                        )}
                        {state.attributesDlgLoading ? (
                            <div><span className="k-icon k-font-icon k-i-loading" /> Loading...</div>
                        ) : (
                            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <table className="k-table k-table-md packlists-treelist" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <colgroup>
                                        <col style={{ width: state.attributesDlgColWidths.packListAttributeValueID + 'px', display: 'none' }} />
                                        <col style={{ width: state.attributesDlgColWidths.attributeID + 'px' }} />
                                        <col style={{ width: state.attributesDlgColWidths.attributeValue + 'px' }} />
                                        <col style={{ width: state.attributesDlgColWidths.lastChangedBy + 'px' }} />
                                        <col style={{ width: state.attributesDlgColWidths.lastChangedOn + 'px' }} />
                                        <col style={{ width: state.attributesDlgColWidths.actions + 'px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={{ display: 'none', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Value ID</th>
                                            <th
                                                style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2, cursor: 'pointer' }}
                                                onClick={() => handleAttributesSortChange('attributeID')}
                                            >
                                                Attribute {state.attributesDlgSort && state.attributesDlgSort[0]?.field === 'attributeID' ? (state.attributesDlgSort[0].dir === 'asc' ? '▲' : '▼') : ''}
                                                <div onMouseDown={e => startColResize('attributes', 'attributeID', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th
                                                style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2, cursor: 'pointer' }}
                                                onClick={() => handleAttributesSortChange('attributeValue')}
                                            >
                                                Value {state.attributesDlgSort && state.attributesDlgSort[0]?.field === 'attributeValue' ? (state.attributesDlgSort[0].dir === 'asc' ? '▲' : '▼') : ''}
                                                <div onMouseDown={e => startColResize('attributes', 'attributeValue', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th
                                                style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2, cursor: 'pointer' }}
                                                onClick={() => handleAttributesSortChange('lastChangedBy')}
                                            >
                                                Last Changed By {state.attributesDlgSort && state.attributesDlgSort[0]?.field === 'lastChangedBy' ? (state.attributesDlgSort[0].dir === 'asc' ? '▲' : '▼') : ''}
                                                <div onMouseDown={e => startColResize('attributes', 'lastChangedBy', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th
                                                style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2, cursor: 'pointer' }}
                                                onClick={() => handleAttributesSortChange('lastChangedOn')}
                                            >
                                                Last Changed On {state.attributesDlgSort && state.attributesDlgSort[0]?.field === 'lastChangedOn' ? (state.attributesDlgSort[0].dir === 'asc' ? '▲' : '▼') : ''}
                                                <div onMouseDown={e => startColResize('attributes', 'lastChangedOn', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th style={{ width: state.attributesDlgColWidths.actions + 'px', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attributesRenderItems.map(item => (
                                                                            <tr key={item.packListAttributeValueID} style={{ borderBottom: '1px solid #eee' }}>
                                                                                <td title={String(item.packListAttributeValueID)} style={{ display: 'none', padding: '4px 8px' }}>{item.packListAttributeValueID}</td>
                                                                                <td title={String(item.attributeID)} style={{ padding: '4px 8px' }}>
                                                                                    <DropDownList
                                                                                        data={state.packlistAttributesForDropDown}
                                                                                        textField="display"
                                                                                        dataItemKey="value"
                                                                                        value={state.packlistAttributesForDropDown.find(d => String(d.value) === String(item.attributeID)) ?? null}
                                                                                        onChange={e => {
                                                                                            const newVal = Number((e.value as any)?.value ?? e.value);
                                                                                            handleAttributeIDChange(item.packListAttributeValueID, newVal);
                                                                                            // commit immediately when attr id changed; pass override to avoid setState race
                                                                                            commitAttributeValue(item.packListAttributeValueID, { attributeID: newVal });
                                                                                        }}
                                                                                        style={{ width: '100%' }}
                                                                                    />
                                                                                </td>
                                                                                <td title={String(item.attributeValue)} style={{ padding: '4px 8px' }}>
                                                                                    {state.attributesDlgSavingIds.has(item.packListAttributeValueID) ? (
                                                                                                                        <span className="k-icon k-font-icon k-i-loading" />
                                                                                                                    ) : (
                                                                                        <input
                                                                                            ref={el => { attributeValueRefs.current[item.packListAttributeValueID] = el; }}
                                                                                            type="text"
                                                                                            value={item.attributeValue}
                                                                                            onChange={e => handleAttributeValueChange(item.packListAttributeValueID, e.target.value)}
                                                                                            onBlur={() => commitAttributeValue(item.packListAttributeValueID)}
                                                                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                                                            style={{ width: '100%' }}
                                                                                        />
                                                                                    )}
                                                                                    {state.attributesDlgItemErrors[item.packListAttributeValueID] && (
                                                                                        <div style={{ color: 'red', fontSize: 12 }}>{state.attributesDlgItemErrors[item.packListAttributeValueID]}</div>
                                                                                    )}
                                                                                </td>
                                                                                <td title={String(item.lastChangedBy ?? '')} style={{ padding: '4px 8px' }}>{item.lastChangedBy ?? ''}</td>
                                                                                <td title={item.lastChangedOn ? String(item.lastChangedOn) : ''} style={{ padding: '4px 8px' }}>{item.lastChangedOn ? new Date(item.lastChangedOn).toLocaleString() : ''}</td>
                                                                                <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                                                                    <button
                                                                                        className="k-button k-button-flat k-button-md"
                                                                                        onClick={() => openAttributesDeleteConfirm(item.packListAttributeValueID)}
                                                                                        disabled={state.attributesDlgSavingIds.has(item.packListAttributeValueID)}
                                                                                        aria-label="Delete attribute"
                                                                                        title="Delete attribute"
                                                                                        style={{ padding: '4px 6px' }}
                                                                                    >
                                                                                        {state.attributesDlgSavingIds.has(item.packListAttributeValueID) ? (
                                                                                            <span className="k-icon k-font-icon k-i-loading" />
                                                                                        ) : (
                                                                                            <span className="k-icon k-font-icon k-i-delete" />
                                                                                        )}
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={addNewAttributeRow} style={{ marginRight: 8 }}>Add Attribute</button>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAttributes}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemsDlgOpen && (
                <Dialog
                    title={`Packlist Items - ${state.menuDataItem?.displayText ?? ''}`}
                    onClose={closePacklistItems}
                    width={980}
                >
                    <div style={{ padding: '8px 0' }}>
                        {state.packlistItemsDlgError && (
                            <div style={{ color: 'red', marginBottom: 8 }}>{state.packlistItemsDlgError}</div>
                        )}
                        {state.packlistItemsDlgLoading ? (
                            <div><span className="k-icon k-font-icon k-i-loading" /> Loading...</div>
                        ) : (
                            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                                <table className="k-table k-table-md packlists-treelist" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <colgroup>
                                        <col style={{ width: state.packlistItemsDlgColWidths.actions + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.packlistItemID + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.itemID + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.itemDesc + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.quantity + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.itemType + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.lastChangedBy + 'px' }} />
                                        <col style={{ width: state.packlistItemsDlgColWidths.lastChangedOn + 'px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={{ width: state.packlistItemsDlgColWidths.actions + 'px', textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', left: 0, background: '#fff', zIndex: 4 }}></th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>PacklistItemID</th>
                                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>ItemID</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Item Description</th>
                                            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Quantity
                                                <div onMouseDown={e => startColResize('packlist', 'quantity', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                                Item Type
                                                <div onMouseDown={e => startColResize('packlist', 'itemType', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                                Last Changed By
                                                <div onMouseDown={e => startColResize('packlist', 'lastChangedBy', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                                                Last Changed On
                                                <div onMouseDown={e => startColResize('packlist', 'lastChangedOn', e)} style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 10, cursor: 'col-resize' }} />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {state.packlistItemsDlgItems.map(item => (
                                            <tr key={item.packlistItemID} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '4px 8px', textAlign: 'center', position: 'sticky', left: 0, background: '#fff', zIndex: 3 }}>
                                                    <button
                                                        className="k-button k-button-flat k-button-md"
                                                        onClick={() => openPLIAttributesForItem(item.packlistItemID)}
                                                        title="Packlist Item Attributes"
                                                        aria-label="Packlist Item Attributes"
                                                        style={{ padding: '6px', minWidth: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                                    >
                                                        <span className="k-icon k-font-icon k-i-list" style={{ fontSize: 14, lineHeight: 1 }} />
                                                        <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'inline-block', marginLeft: 2 }}>
                                                            <path d="M8 6h13M8 12h13M8 18h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                                            <path d="M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                </td>
                                                <td title={String(item.packlistItemID)} style={{ padding: '4px 8px' }}>{item.packlistItemID}</td>
                                                <td title={String(item.itemID)} style={{ padding: '4px 8px', textAlign: 'right' }}>{item.itemID}</td>
                                                <td title={String(item.itemDesc)} style={{ padding: '4px 8px' }}>{item.itemDesc}</td>
                                                <td title={String(item.quantity)} style={{ padding: '4px 8px', textAlign: 'right' }}>
                                                    {state.packlistItemsDlgSavingIds.has(item.packlistItemID) ? (
                                                        <span className="k-icon k-font-icon k-i-loading" />
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={e => handlePacklistItemQtyChange(item.packlistItemID, Number(e.target.value))}
                                                            onBlur={() => commitPacklistItemQty(item.packlistItemID)}
                                                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                            style={{ width: 80, textAlign: 'right' }}
                                                        />
                                                    )}
                                                    {state.packlistItemsDlgItemErrors[item.packlistItemID] && (
                                                        <div style={{ color: 'red', fontSize: 12 }}>{state.packlistItemsDlgItemErrors[item.packlistItemID]}</div>
                                                    )}
                                                </td>
                                                <td title={String(item.itemType)} style={{ padding: '4px 8px' }}>{item.itemType}</td>
                                                <td title={String(item.lastChangedBy ?? '')} style={{ padding: '4px 8px' }}>{item.lastChangedBy ?? ''}</td>
                                                <td title={item.lastChangedOn ? String(item.lastChangedOn) : ''} style={{ padding: '4px 8px' }}>{item.lastChangedOn ? new Date(item.lastChangedOn).toLocaleString() : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePacklistItems}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.packlistItemAttributesDlgOpen && (
                <Dialog
                    title={`Packlist Item Attributes`}
                    onClose={closePLIAttributesDlg}
                    width={820}
                >
                    <div style={{ padding: '8px 0' }}>
                        {state.packlistItemAttributesDlgError && (
                            <div style={{ color: 'red', marginBottom: 8 }}>{state.packlistItemAttributesDlgError}</div>
                        )}
                        {state.packlistItemAttributesDlgLoading ? (
                            <div><span className="k-icon k-font-icon k-i-loading" /> Loading...</div>
                        ) : (
                            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                <table className="k-table k-table-md packlists-treelist" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <colgroup>
                                        <col style={{ width: state.packlistItemAttributesDlgColWidths.attributeID + 'px' }} />
                                        <col style={{ width: state.packlistItemAttributesDlgColWidths.attributeValue + 'px' }} />
                                        <col style={{ width: state.packlistItemAttributesDlgColWidths.lastChangedBy + 'px' }} />
                                        <col style={{ width: state.packlistItemAttributesDlgColWidths.lastChangedOn + 'px' }} />
                                        <col style={{ width: state.packlistItemAttributesDlgColWidths.actions + 'px' }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Attribute</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Value</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Last Changed By</th>
                                            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>Last Changed On</th>
                                            <th style={{ width: state.packlistItemAttributesDlgColWidths.actions + 'px', textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #ccc', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {state.packlistItemAttributesDlgItems.map(item => {
                                            return (
                                                <tr key={item.packListAttributeValueID} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td title={String(item.attributeID)} style={{ padding: '4px 8px' }}>
                                                        <DropDownList
                                                            data={state.attributesForDropDown}
                                                            textField="display"
                                                            dataItemKey="value"
                                                            value={state.attributesForDropDown.find(d => String(d.value) === String(item.attributeID)) ?? null}
                                                            onChange={e => {
                                                                const newVal = Number((e.value as any)?.value ?? e.value);
                                                                handlePLIAttributeIDChange(item.packListAttributeValueID, newVal);
                                                                commitPLIAttributeValue(item.packListAttributeValueID, { attributeID: newVal });
                                                            }}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </td>
                                                    <td title={String(item.attributeValue)} style={{ padding: '4px 8px' }}>
                                                        {state.packlistItemAttributesDlgSavingIds.has(item.packListAttributeValueID) ? (
                                                            <span className="k-icon k-font-icon k-i-loading" />
                                                        ) : (
                                                            <input
                                                                ref={el => { attributeValueRefs.current[item.packListAttributeValueID] = el; }}
                                                                type="text"
                                                                value={item.attributeValue}
                                                                onChange={e => handlePLIAttributeValueChange(item.packListAttributeValueID, e.target.value)}
                                                                onBlur={() => commitPLIAttributeValue(item.packListAttributeValueID)}
                                                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                                style={{ width: '100%' }}
                                                            />
                                                        )}
                                                        {state.packlistItemAttributesDlgItemErrors[item.packListAttributeValueID] && (
                                                            <div style={{ color: 'red', fontSize: 12 }}>{state.packlistItemAttributesDlgItemErrors[item.packListAttributeValueID]}</div>
                                                        )}
                                                    </td>
                                                    <td title={String(item.lastChangedBy ?? '')} style={{ padding: '4px 8px' }}>{item.lastChangedBy ?? ''}</td>
                                                    <td title={item.lastChangedOn ? String(item.lastChangedOn) : ''} style={{ padding: '4px 8px' }}>{item.lastChangedOn ? new Date(item.lastChangedOn).toLocaleString() : ''}</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                                        <button
                                                            className="k-button k-button-flat k-button-md"
                                                            onClick={() => openPacklistItemAttributesDeleteConfirm(item.packListAttributeValueID)}
                                                            disabled={state.packlistItemAttributesDlgSavingIds.has(item.packListAttributeValueID)}
                                                            aria-label="Delete attribute"
                                                            title="Delete attribute"
                                                            style={{ padding: '4px 6px' }}
                                                        >
                                                            {state.packlistItemAttributesDlgSavingIds.has(item.packListAttributeValueID) ? (
                                                                <span className="k-icon k-font-icon k-i-loading" />
                                                            ) : (
                                                                <span className="k-icon k-font-icon k-i-delete" />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePLIAttributesDlg}>Close</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.packlistItemAttributesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closePacklistItemAttributesDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this attribute?
                    </p>
                    {state.packlistItemAttributesDeleteError && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.packlistItemAttributesDeleteError}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closePacklistItemAttributesDeleteConfirm} disabled={state.packlistItemAttributesDeleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmPacklistItemAttributesDelete}
                            disabled={state.packlistItemAttributesDeleteInProgress}
                        >
                            {state.packlistItemAttributesDeleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.deleteConfirmItem && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete <strong>{state.deleteConfirmItem?.displayText}</strong> and ALL its children?
                    </p>
                    {state.deleteErrorMsg && (
                        <div style={{ color: 'red', marginBottom: '8px' }}>{state.deleteErrorMsg}</div>
                    )}
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmDelete}
                            disabled={state.deleteInProgress}
                        >
                            {state.deleteInProgress ? <span className="k-icon k-font-icon k-i-loading" /> : 'Delete'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
            {state.newPacklistDlgOpen && (
                <Dialog
                    title="New Packlist"
                    onClose={closeNewPacklistDlg}
                    width={480}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                        {state.newPacklistDlgError && (
                            <div style={{ color: 'red' }}>{state.newPacklistDlgError}</div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Code</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newPacklistDlgCode}
                                onChange={e => setState(prev => ({ ...prev, newPacklistDlgCode: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Description</label>
                            <input
                                className="k-input k-input-md k-rounded-md"
                                value={state.newPacklistDlgDesc}
                                onChange={e => setState(prev => ({ ...prev, newPacklistDlgDesc: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>Type</label>
                            <DropDownList
                                data={state.packlistTypes}
                                textField="display"
                                dataItemKey="value"
                                value={state.packlistTypes.find(t => t.value === state.newPacklistDlgTypeID) ?? null}
                                onChange={e => setState(prev => ({ ...prev, newPacklistDlgTypeID: e.value.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeNewPacklistDlg} disabled={state.newPacklistDlgSaving}>Cancel</button>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-primary"
                            onClick={submitNewPacklist}
                            disabled={state.newPacklistDlgSaving}
                        >
                            {state.newPacklistDlgSaving ? <span className="k-icon k-font-icon k-i-loading" /> : 'Create'}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

export default connector(Packlists);
